import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    MessageSquare,
    Send,
    Search,
    Filter,
    Paperclip,
    AtSign,
    AlertCircle,
    CheckCircle,
    Clock,
    Star,
    Reply
} from 'lucide-react';

const commentCategories = [
    { value: 'general', label: 'Général', color: 'bg-gray-100 text-gray-800' },
    { value: 'technical', label: 'Technique', color: 'bg-blue-100 text-blue-800' },
    { value: 'financial', label: 'Financier', color: 'bg-green-100 text-green-800' },
    { value: 'planning', label: 'Planning', color: 'bg-purple-100 text-purple-800' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' }
];

const comments = [
    {
        id: 1,
        author: 'Marie Rakoto',
        role: 'Chef de Projet',
        avatar: 'MR',
        content: 'Mise à jour sur l\'avancement des appels d\'offres équipements. Nous avons reçu 4 propositions techniques conformes. L\'analyse comparative sera finalisée d\'ici vendredi.',
        timestamp: '2025-01-20T14:30:00',
        category: 'general',
        reactions: { likes: 3, important: 1 },
        attachments: ['AO_Comparatif_v2.xlsx'],
        mentions: ['Paul Andry', 'Sophie Ranaivo'],
        replies: [
            {
                id: 11,
                author: 'Paul Andry',
                avatar: 'PA',
                content: 'Parfait Marie, j\'ai commencé l\'évaluation technique. Le fournisseur Tetra Pak semble avoir la meilleure proposition.',
                timestamp: '2025-01-20T15:45:00'
            }
        ]
    },
    {
        id: 2,
        author: 'Sophie Ranaivo',
        role: 'Direction Financière',
        avatar: 'SR',
        content: 'Attention : le budget initial semble dépassé de 15% selon les premières estimations. Nous devons revoir les spécifications ou demander un budget complémentaire.',
        timestamp: '2025-01-20T11:15:00',
        category: 'urgent',
        reactions: { likes: 0, important: 5 },
        attachments: ['Budget_Reforecast_Jan2025.pdf'],
        mentions: ['Marie Rakoto', 'Jean Rakotoarison'],
        replies: []
    },
    {
        id: 3,
        author: 'Paul Andriamahefa',
        role: 'Direction Technique',
        avatar: 'PA',
        content: 'Validation technique positive pour la ligne de production. Les spécifications respectent nos standards qualité. Quelques ajustements mineurs à prévoir sur les systèmes de contrôle.',
        timestamp: '2025-01-19T16:20:00',
        category: 'technical',
        reactions: { likes: 2, important: 2 },
        attachments: ['Validation_Technique_v1.pdf', 'Schemas_Controle.dwg'],
        mentions: [],
        replies: []
    },
    {
        id: 4,
        author: 'Jean Rakotoarison',
        role: 'Validation Métier',
        avatar: 'JR',
        content: 'Le business case est solide. ROI estimé à 18.5% sur 5 ans. Impact positif attendu sur la capacité de production (+30%) et la réduction des coûts unitaires (-12%). Validation métier approuvée.',
        timestamp: '2025-01-19T09:30:00',
        category: 'financial',
        reactions: { likes: 4, important: 3 },
        attachments: ['Business_Case_Final.pptx'],
        mentions: ['Sophie Ranaivo'],
        replies: [
            {
                id: 41,
                author: 'Hery Rasolofo',
                avatar: 'HR',
                content: 'Excellent travail Jean. Ces chiffres sont conformes à nos attentes stratégiques.',
                timestamp: '2025-01-19T10:15:00'
            }
        ]
    }
];

const categoryConfig = commentCategories.reduce((acc, cat) => {
    acc[cat.value] = cat;
    return acc;
}, {});

export default function CommentsTab({ project }) {
    const [newComment, setNewComment] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('general');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [replyTo, setReplyTo] = useState(null);

    const filteredComments = comments.filter(comment => {
        const matchesSearch = comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
            comment.author.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || comment.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    const handleSubmitComment = () => {
        if (newComment.trim()) {
            // Logic to submit new comment
            console.log('Submitting comment:', { content: newComment, category: selectedCategory });
            setNewComment('');
            setSelectedCategory('general');
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);

        if (diffInHours < 1) {
            return `Il y a ${Math.floor(diffInHours * 60)} minutes`;
        } else if (diffInHours < 24) {
            return `Il y a ${Math.floor(diffInHours)} heures`;
        } else {
            return date.toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    return (
        <div className="space-y-6">
            {/* Comments Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-teal-600 mb-1">Total commentaires</p>
                                <p className="text-3xl font-bold text-teal-900">{comments.length}</p>
                            </div>
                            <div className="w-12 h-12 bg-teal-200 rounded-2xl flex items-center justify-center">
                                <MessageSquare className="w-6 h-6 text-teal-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600 mb-1">Participants actifs</p>
                                <p className="text-3xl font-bold text-blue-900">5</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-200 rounded-2xl flex items-center justify-center">
                                <AtSign className="w-6 h-6 text-blue-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-red-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-red-600 mb-1">Commentaires urgents</p>
                                <p className="text-3xl font-bold text-red-900">
                                    {comments.filter(c => c.category === 'urgent').length}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-red-200 rounded-2xl flex items-center justify-center">
                                <AlertCircle className="w-6 h-6 text-red-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-green-600 mb-1">Taux de réponse</p>
                                <p className="text-3xl font-bold text-green-900">85%</p>
                            </div>
                            <div className="w-12 h-12 bg-green-200 rounded-2xl flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-green-700" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search and Filters */}
            <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                placeholder="Rechercher dans les commentaires..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Filtrer par catégorie" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les catégories</SelectItem>
                                {commentCategories.map(category => (
                                    <SelectItem key={category.value} value={category.value}>
                                        {category.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* New Comment Form */}
            <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <Avatar className="w-10 h-10">
                                <AvatarFallback className="bg-teal-600 text-white">MR</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 space-y-3">
                                <Textarea
                                    placeholder="Ajoutez votre commentaire..."
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    rows={3}
                                    className="resize-none"
                                />
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                                            <SelectTrigger className="w-40">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {commentCategories.map(category => (
                                                    <SelectItem key={category.value} value={category.value}>
                                                        {category.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Button variant="outline" size="sm" className="gap-2">
                                            <Paperclip className="w-4 h-4" />
                                            Pièce jointe
                                        </Button>
                                        <Button variant="outline" size="sm" className="gap-2">
                                            <AtSign className="w-4 h-4" />
                                            Mentionner
                                        </Button>
                                    </div>
                                    <Button onClick={handleSubmitComment} className="bg-teal-600 hover:bg-teal-700 gap-2">
                                        <Send className="w-4 h-4" />
                                        Publier
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Comments Thread */}
            <div className="space-y-4">
                {filteredComments.map(comment => (
                    <Card key={comment.id} className="border-0 shadow-lg">
                        <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                                <Avatar className="w-10 h-10">
                                    <AvatarFallback className="bg-teal-600 text-white">
                                        {comment.avatar}
                                    </AvatarFallback>
                                </Avatar>

                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="font-semibold text-gray-900">{comment.author}</span>
                                        <Badge variant="outline" className="text-xs">
                                            {comment.role}
                                        </Badge>
                                        <Badge className={categoryConfig[comment.category]?.color}>
                                            {categoryConfig[comment.category]?.label}
                                        </Badge>
                                        <span className="text-sm text-gray-500">
                      {formatTimestamp(comment.timestamp)}
                    </span>
                                    </div>

                                    <div className="prose prose-sm max-w-none text-gray-700 mb-3">
                                        {comment.content}
                                    </div>

                                    {comment.mentions.length > 0 && (
                                        <div className="mb-3">
                                            <span className="text-xs text-gray-500">Mentions: </span>
                                            {comment.mentions.map((mention, idx) => (
                                                <Badge key={idx} variant="outline" className="text-xs mr-1">
                                                    @{mention}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}

                                    {comment.attachments.length > 0 && (
                                        <div className="mb-3">
                                            <span className="text-xs text-gray-500 block mb-1">Pièces jointes:</span>
                                            {comment.attachments.map((attachment, idx) => (
                                                <Badge key={idx} variant="outline" className="text-xs mr-1 cursor-pointer hover:bg-gray-100">
                                                    <Paperclip className="w-3 h-3 mr-1" />
                                                    {attachment}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                        <button className="flex items-center gap-1 hover:text-gray-700">
                                            <Star className="w-4 h-4" />
                                            {comment.reactions.likes} J'aime
                                        </button>
                                        <button className="flex items-center gap-1 hover:text-gray-700">
                                            <AlertCircle className="w-4 h-4" />
                                            {comment.reactions.important} Important
                                        </button>
                                        <button
                                            className="flex items-center gap-1 hover:text-gray-700"
                                            onClick={() => setReplyTo(comment.id)}
                                        >
                                            <Reply className="w-4 h-4" />
                                            Répondre
                                        </button>
                                    </div>

                                    {/* Replies */}
                                    {comment.replies.length > 0 && (
                                        <div className="mt-4 ml-6 space-y-3 border-l-2 border-gray-100 pl-4">
                                            {comment.replies.map(reply => (
                                                <div key={reply.id} className="flex items-start gap-3">
                                                    <Avatar className="w-8 h-8">
                                                        <AvatarFallback className="bg-gray-600 text-white text-xs">
                                                            {reply.avatar}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="font-medium text-sm text-gray-900">{reply.author}</span>
                                                            <span className="text-xs text-gray-500">
                                {formatTimestamp(reply.timestamp)}
                              </span>
                                                        </div>
                                                        <p className="text-sm text-gray-700">{reply.content}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}